import React, { FC, useState, useEffect, useRef } from 'react'

import useEvent from 'Hooks/useEvent'

import { Context, ContextInterface, FieldEventCallback } from './Context'
import {
  SprField,
  SprNumberFormat,
  SprInitOptions,
  SprTokenizeCreditCardFields,
  SprSetRecacheOptions,
  SprEvent,
  SprErrorsCallback,
} from './types'

export declare class SpreedlyPaymentFrame {
  constructor()

  init(environmentKey: string, options: SprInitOptions): void
  reload(): void
  removeHandlers(): void

  tokenizeCreditCard(fields: SprTokenizeCreditCardFields): void
  validate(): void

  setRecache(environmentKey: string, options: SprSetRecacheOptions): void
  recache(): void

  setFieldType(field: SprField, type: 'number' | 'text' | 'tel'): void
  setLabel(field: SprField, label: string): void
  setNumberFormat(format: SprNumberFormat): void
  setPlaceholder(field: SprField, placeholder: string): void
  setStyle(field: SprField, css: string): void
  transferFocus(field: SprField): void

  on: SprEvent
}

export declare const Spreedly: InstanceType<typeof SpreedlyPaymentFrame>

interface Props {
  children?: React.ReactNode
  environmentKey: string
  formId?: string
  getTokenizeCreditCardFields(): Promise<SprTokenizeCreditCardFields>
  inputStyles?: { cvv: string; number: string }
  onErrors?: SprErrorsCallback
  paymentFormError?: (error?: string) => void
  paymentFormReady?: () => void
  placeholderCVV?: string
  placeholderNumber?: string
  tokenReceived(token: string, paymentMethod: Record<string, unknown>): void
  transferFocus?: (field: SprField) => string
}

const emptyFunction = () => {}

export const PaymentForm: FC<Props> = ({
  children = null,
  environmentKey,
  formId = 'spr-payment-form',
  getTokenizeCreditCardFields,
  onErrors: handleError = () => {},
  paymentFormError = () => {},
  tokenReceived,
  ...props
}: Props) => {
  const [isErrored, setErrored] = useState(false)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [paymentForm, setPaymentForm] = useState<typeof Spreedly>()
  const [formReady, setFormReady] = useState(false)
  const fieldEventNamesRef = useRef<Partial<Record<SprField, FieldEventCallback>>>({})

  const paymentFormReady = () => {
    setFormReady(true)
    props.paymentFormReady?.()
  }

  function loadSprPaymentFormLibrary(onSuccess: () => void, onError: OnErrorEventHandlerNonNull): void {
    if (document.getElementById('spr-payment-form-script') && typeof SpreedlyPaymentFrame === 'function') {
      return onSuccess?.()
    }
    const script = document.createElement('script')
    script.id = 'spr-payment-form-script'
    script.src = 'https://core.spreedly.com/iframe/iframe-v1.min.js'
    script.onload = () => onSuccess()
    script.onerror = (...args) => onError(...args)
    document.body.appendChild(script)
  }

  function renderSprPaymentForm(): void {
    if (!scriptLoaded || paymentForm || isErrored) {
      return
    }
    try {
      const newPaymentForm = new SpreedlyPaymentFrame()
      newPaymentForm.init(environmentKey, {
        numberEl: `${formId}-spr-number`,
        cvvEl: `${formId}-spr-cvv`,
      })
      setPaymentForm(newPaymentForm)
      newPaymentForm.on('ready', paymentFormReady)
      newPaymentForm.on('errors', handleError)
    } catch (error) {
      console.error(error)
      setErrored(true)
      paymentFormError('Unable to init Spreedly payment form')
    }
  }

  useEffect(() => {
    loadSprPaymentFormLibrary(
      () => setScriptLoaded(true),
      (...args) => {
        console.error(...args)
        setErrored(true)
        paymentFormError('Unable to load Spreedly payment library')
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    renderSprPaymentForm()
    return () => {
      if (paymentForm) {
        paymentForm.removeHandlers()
        setPaymentForm(undefined)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptLoaded])

  const handleTokenReceived = useEvent(tokenReceived)

  useEffect(() => {
    if (formReady) {
      paymentForm?.on('paymentMethod', handleTokenReceived)
      paymentForm?.on('fieldEvent', (name, ...args) => fieldEventNamesRef.current[name]?.(...args))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formReady])

  const tokenizeCreditCard = useEvent(
    () =>
      void getTokenizeCreditCardFields()
        .then((fields) => paymentForm?.tokenizeCreditCard(fields))
        .catch(() => handleError([])),
  )

  const handleFieldEvent = useEvent((field: SprField, callback: FieldEventCallback) => {
    const b = fieldEventNamesRef.current
    b[field] = callback
  })

  if (isErrored)
    return (
      <div className="spr-payment-form" id={formId}>
        {children}
      </div>
    )

  const context: ContextInterface = {
    formId,
    onField: handleFieldEvent,
    tokenizeCreditCard,
    setFieldType: paymentForm?.setFieldType.bind(paymentForm) || emptyFunction,
    setLabel: paymentForm?.setLabel.bind(paymentForm) || emptyFunction,
    setPlaceholder: paymentForm?.setPlaceholder.bind(paymentForm) || emptyFunction,
    setStyle: paymentForm?.setStyle.bind(paymentForm) || emptyFunction,
    setNumberFormat: paymentForm?.setNumberFormat.bind(paymentForm) || emptyFunction,
    transferFocus: paymentForm?.transferFocus.bind(paymentForm) || emptyFunction,
  }

  return (
    <Context.Provider value={context}>
      <div className="spr-payment-form" id={formId}>
        {children}
      </div>
    </Context.Provider>
  )
}
